import React from 'react';
import { ScrollView } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import {
  CheckCircle2,
  Calendar,
  MapPin,
  Users,
  Banknote,
} from 'lucide-react-native';
import { CustomButton, Screen, ScreenHeader } from '@/components';
import { COLORS } from '@/constants/colors';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '@/types/navigation';
import { resetToHome } from '@/navigation/navigationRef';

type ApplicationSentRouteProp = RouteProp<
  RootStackParamList,
  'ApplicationSent'
>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const ApplicationSentScreen: React.FC = () => {
  const themeName = useThemeName();
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<ApplicationSentRouteProp>();
  const { announcementId, announcement, application } = route.params || {};

  const isDark = themeName === 'dark';
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const titleColor = themeName === 'dark' ? '#ffffff' : '#18181b';
  const subtitleColor = themeName === 'dark' ? '#a1a1aa' : '#18181b';
  const softBorder =
    themeName === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';

  const handleGoBack = () => {
    resetToHome();
  };

  const handleGoToMyApplications = () => {
    navigation.navigate('MyApplications');
  };

  const handleSearchOther = () => {
    resetToHome();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = [
      'stycznia',
      'lutego',
      'marca',
      'kwietnia',
      'maja',
      'czerwca',
      'lipca',
      'sierpnia',
      'września',
      'października',
      'listopada',
      'grudnia',
    ];
    return `${day} ${months[date.getMonth()]}`;
  };

  const formatTime = (timeStr: string | null | undefined) => {
    if (!timeStr) return '';
    // Handle both HH:MM:SS and HH:MM formats
    return timeStr.substring(0, 5);
  };

  const formatParticipantCount = (count: number) => {
    if (count === 1) return '1';
    return `${count} osób`;
  };

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('screens.applicationSent.title', 'Zgłoszenie wysłane')}
          onBackPress={handleGoBack}
        />

        <ScrollView
          contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
        >
          <YStack space="$4">
            <YStack
              backgroundColor="$backgroundStrong"
              borderRadius="$6"
              padding="$5"
              space="$4"
              borderWidth={1}
              borderColor={softBorder}
            >
              <YStack alignItems="center" space="$2">
                <YStack
                  alignSelf="center"
                  width={180}
                  height={180}
                  borderRadius={90}
                  alignItems="center"
                  justifyContent="center"
                  marginBottom="$1"
                >
                  <CheckCircle2
                    size={120}
                    color={themeName === 'dark' ? '#ffffff' : '#111827'}
                    strokeWidth={0.5}
                  />
                </YStack>
                <Text
                  fontSize={27}
                  fontWeight="bold"
                  fontFamily="Poppins"
                  color={titleColor}
                  textAlign="center"
                >
                  {t('screens.applicationSent.sentTitle')}
                </Text>
                <Text
                  fontSize={16}
                  fontFamily="Poppins"
                  fontWeight="normal"
                  color={subtitleColor}
                  textAlign="center"
                >
                  {t('screens.applicationSent.sentSubtitle')}
                </Text>
              </YStack>

              {announcement && (
                <YStack
                  borderWidth={1}
                  borderColor={softBorder}
                  borderRadius="$4"
                  padding="$4"
                  space="$3"
                >
                  <Text
                    fontSize={18}
                    fontFamily="Poppins"
                    fontWeight="600"
                    color={titleColor}
                  >
                    {announcement.additionalInfo || announcement.meetingType?.name || t('screens.applicationSent.eventTitle')}
                  </Text>

                  <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="$2"
                  >
                    <XStack alignItems="center" space="$3">
                      <Calendar size={18} color={subtitleColor} />
                      <Text
                        fontSize={14}
                        fontFamily="Poppins"
                        fontWeight="normal"
                        color={subtitleColor}
                      >
                        {formatDate(announcement.meetingDate)},{' '}
                        {formatTime(announcement.meetingTime)}
                      </Text>
                    </XStack>
                    <XStack alignItems="center" space="$3">
                      <MapPin size={18} color={subtitleColor} />
                      <Text
                        fontSize={14}
                        fontFamily="Poppins"
                        fontWeight="normal"
                        color={subtitleColor}
                      >
                        {announcement.meetingAddress}
                      </Text>
                    </XStack>
                  </XStack>

                  <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center" space="$3">
                      <Users size={18} color={subtitleColor} />
                      <Text
                        fontSize={14}
                        fontFamily="Poppins"
                        fontWeight="normal"
                        color={subtitleColor}
                      >
                        {formatParticipantCount(announcement.participantCount)}
                      </Text>
                    </XStack>
                    {(application?.proposedAmount || announcement.paymentAmount) && (
                      <XStack alignItems="center" space="$2">
                        <Banknote size={18} color={subtitleColor} />
                        <Text
                          fontSize={26}
                          fontWeight="bold"
                          fontFamily="Poppins"
                          color={titleColor}
                        >
                          zł {application?.proposedAmount || announcement.paymentAmount}
                        </Text>
                      </XStack>
                    )}
                  </XStack>
                </YStack>
              )}

              <YStack
                backgroundColor={'rgba(52,168,83,0.23)'}
                borderRadius="$4"
                padding="$4"
                space="$2"
                borderWidth={1}
                borderColor={'rgba(52,168,83,0.35)'}
              >
                <Text
                  fontSize={18}
                  fontFamily="Poppins"
                  fontWeight="500"
                  color="#34A853"
                  textAlign="left"
                >
                  {t('screens.applicationSent.nextStepsTitle')}
                </Text>
                <XStack
                  alignItems="center"
                  space="$3"
                  justifyContent="flex-start"
                >
                  <YStack
                    width={24}
                    height={24}
                    borderRadius={12}
                    backgroundColor="#34A853"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize={12} color="white" fontWeight="600">
                      1
                    </Text>
                  </YStack>
                  <Text fontSize={14} color="#34A853" textAlign="left" flex={1}>
                    {t('screens.applicationSent.nextStep1')}
                  </Text>
                </XStack>
                <XStack
                  alignItems="center"
                  space="$3"
                  justifyContent="flex-start"
                >
                  <YStack
                    width={24}
                    height={24}
                    borderRadius={12}
                    backgroundColor="#34A853"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize={12} color="white" fontWeight="600">
                      2
                    </Text>
                  </YStack>
                  <Text fontSize={14} color="#34A853" textAlign="left" flex={1}>
                    {t('screens.applicationSent.nextStep2')}
                  </Text>
                </XStack>
                <XStack
                  alignItems="center"
                  space="$3"
                  justifyContent="flex-start"
                >
                  <YStack
                    width={24}
                    height={24}
                    borderRadius={12}
                    backgroundColor="#34A853"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize={12} color="white" fontWeight="600">
                      3
                    </Text>
                  </YStack>
                  <Text fontSize={14} color="#34A853" textAlign="left" flex={1}>
                    {t('screens.applicationSent.nextStepNote')}
                  </Text>
                </XStack>
              </YStack>

              <YStack
                backgroundColor={'rgba(245,158,11,0.12)'}
                borderRadius="$4"
                padding="$4"
                space="$2"
                borderWidth={1}
                borderColor={'rgba(245,158,11,0.25)'}
              >
                <Text
                  fontSize={18}
                  fontFamily="Poppins"
                  fontWeight="500"
                  color="#9E844D"
                >
                  {t('screens.applicationSent.responseTimeTitle')}
                </Text>
                <Text fontSize={14} color="#9E844D">
                  {t('screens.applicationSent.responseTimeDesc')}
                </Text>
              </YStack>

              <YStack marginTop="$3" space="$2">
                <CustomButton
                  backgroundColor="#5E809C"
                  color="white"
                  paddingVertical={14}
                  borderRadius={12}
                  onPress={handleGoToMyApplications}
                >
                  {t('screens.applicationSent.goToMyApplications')}
                </CustomButton>

                <CustomButton
                  backgroundColor="transparent"
                  color="#5E809C"
                  paddingVertical={14}
                  borderRadius={12}
                  onPress={handleSearchOther}
                >
                  <YStack
                    width="100%"
                    backgroundColor="transparent"
                    borderWidth={1}
                    borderColor="#5E809C"
                    borderRadius={12}
                    paddingVertical={14}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="#5E809C">
                      {t('screens.applicationSent.searchOther')}
                    </Text>
                  </YStack>
                </CustomButton>
              </YStack>
            </YStack>
          </YStack>
        </ScrollView>
      </YStack>
    </Screen>
  );
};

export default ApplicationSentScreen;
